import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_ORIGIN = "https://www.elevestpartners.de";
const TITLE_MAX = 60;
const DESC_MAX = 160;

/** Smart-trim: strip "| Elevest Partners" brand suffix first, then word-boundary truncate with ellipsis. */
function clampTitle(raw: string): string {
  let t = raw.trim();
  if (t.length <= TITLE_MAX) return t;
  // Strip common brand suffixes
  t = t.replace(/\s*[|–—-]\s*Elevest Partners\s*$/i, "").trim();
  if (t.length <= TITLE_MAX) return t;
  // Word-boundary truncate
  const slice = t.slice(0, TITLE_MAX - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 30 ? slice.slice(0, lastSpace) : slice).replace(/[\s.,;:–—-]+$/, "") + "…";
}

function clampDescription(raw: string): string {
  const d = raw.trim();
  if (d.length <= DESC_MAX) return d;
  const slice = d.slice(0, DESC_MAX - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 80 ? slice.slice(0, lastSpace) : slice).replace(/[\s.,;:–—-]+$/, "") + "…";
}

function setMeta(selector: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/\[(.+?)="(.+?)"\]/) ?? [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

/**
 * Sitewide SEO meta normalizer.
 *
 * 1. Clamps document.title to ≤60 chars (strips brand suffix first, then word-boundary truncates).
 * 2. Clamps meta description to ≤160 chars.
 * 3. Mirrors title/description into og:* and twitter:* tags so JS-executing crawlers
 *    (e.g. Googlebot) see per-route social previews.
 * 4. Keeps og:url synced with the current path.
 *
 * Note: Social crawlers like LinkedIn, Slack, Facebook and Twitter do NOT execute JS
 * and will keep seeing the static index.html tags. Truly per-route social previews on
 * those platforms require SSR or build-time prerendering.
 */
const NOINDEX_PREFIXES = ["/beratung", "/investments/", "/kooperationen/"];

const RouteMetaSync = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const sync = () => {
      // Toggle robots noindex for private routes
      const shouldNoindex = NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
      const robotsEl = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
      if (shouldNoindex) {
        setMeta('meta[name="robots"]', "noindex, nofollow");
      } else if (robotsEl) {
        robotsEl.remove();
      }

      // Clamp title
      const currentTitle = document.title;
      const clampedTitle = clampTitle(currentTitle);
      if (clampedTitle !== currentTitle) document.title = clampedTitle;

      // Clamp description
      const descEl = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
      const currentDesc = descEl?.getAttribute("content") ?? "";
      const clampedDesc = clampDescription(currentDesc);
      if (descEl && clampedDesc !== currentDesc) descEl.setAttribute("content", clampedDesc);

      // Sync to og:* / twitter:*
      // WEB-57: og:url mirrors the parameterless canonical (no ?lang / query).
      const url = `${SITE_ORIGIN}${pathname}`;
      if (clampedTitle) {
        setMeta('meta[property="og:title"]', clampedTitle);
        setMeta('meta[name="twitter:title"]', clampedTitle);
      }
      if (clampedDesc) {
        setMeta('meta[property="og:description"]', clampedDesc);
        setMeta('meta[name="twitter:description"]', clampedDesc);
      }
      setMeta('meta[property="og:url"]', url);

      // Self-referencing canonical for the current route. Route-level hooks
      // (e.g. useArticleSeo) may override this with a more specific URL.
      const canonicalUrl = `${SITE_ORIGIN}${pathname}`;
      let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      if (canonicalEl.getAttribute("href") !== canonicalUrl) {
        canonicalEl.setAttribute("href", canonicalUrl);
      }
    };

    // Run after route-level useEffects that set title/description.
    const raf = requestAnimationFrame(() => {
      sync();
      // Second pass for routes that set meta inside lazy children.
      window.setTimeout(sync, 60);
    });

    return () => cancelAnimationFrame(raf);
  }, [pathname, search]);

  return null;
};

export default RouteMetaSync;
